import {
    DeleteForeverOutlined,
    Edit,
    HlsOutlined,
    KeyboardArrowDown,
    KeyboardArrowUp,
    KeyOutlined,
    LinkOutlined,
    ListOutlined,
    Lock,
    PlayArrow,
    PublicOutlined,
    SettingsOutlined,
    Stop,
} from "@mui/icons-material";
import {
    GetV2Channel_getV2Channel,
    GetV2Channel_getV2Channel_engines,
    GetV2Channel_getV2Channel_ingests,
} from "./__generated__/GetV2Channel";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import AuthService from "../../../../auth/AuthService";
import { useMutation } from "react-apollo";
import { DeleteV2EngineMutation, DeleteV2EngineMutationVariables } from "./__generated__/DeleteV2EngineMutation";
import { DELETE_V2_ENGINE, GET_V2_CHANNEL, START_V2_ENGINE, STOP_V2_ENGINE } from "./V2ChannelDetails";
import { toast } from "react-toastify";
import { GraphQLError } from "graphql";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import { StartV2EngineMutation, StartV2EngineMutationVariables } from "./__generated__/StartV2EngineMutation";
import { StopV2EngineMutation, StopV2EngineMutationVariables } from "./__generated__/StopV2EngineMutation";
import UpdateEngineBasicsDialog from "./Engine/UpdateEngineBasicsDialog";
import gql from "graphql-tag";
import UpdateEngineDialog from "./Engine/UpdateEngineDialog";
import { EngineValues } from "./Engine/UpdateEngineForm";
import UpdateEngineOverrideDialog from "./Engine/UpdateEngineOverrideDialog";
import moment from "moment";

interface Props {
    engine: GetV2Channel_getV2Channel_engines;
    ingests: GetV2Channel_getV2Channel_ingests[];
    onHover: () => void;
    organizationId: string;
    channel: GetV2Channel_getV2Channel;
    onStopHover: () => void;
    highlight?: boolean;
}

export interface Affinity {
    sdkType: string;
    deviceType: string;
    osName: string;
}

export default function V2ChannelEngineDetails({
    engine: engineInput,
    ingests,
    onHover,
    onStopHover,
    highlight,
    channel,
    organizationId,
}: Props) {
    const [showMore, setShowMore] = useState<boolean>(false);

    const { id: channelId } = channel;

    const [engine, setEngine] = useState<Omit<GetV2Channel_getV2Channel_engines, "__typename">>(engineInput);

    useEffect(() => {
        setEngine(engineInput);
    }, [engineInput]);

    const canRemoveAndUpdateEngine = engine.status === "stopped";
    const [deleteEngine] = useMutation<DeleteV2EngineMutation, DeleteV2EngineMutationVariables>(DELETE_V2_ENGINE);
    const [startEngine, { loading: startLoading }] = useMutation<StartV2EngineMutation, StartV2EngineMutationVariables>(
        START_V2_ENGINE,
    );
    const [stopEngine, { loading: stopLoading }] = useMutation<StopV2EngineMutation, StopV2EngineMutationVariables>(
        STOP_V2_ENGINE,
    );

    const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
    const [openConfirmStart, setOpenConfirmStart] = useState<boolean>(false);
    const [openConfirmStop, setOpenConfirmStop] = useState<boolean>(false);
    const [openUpdateBasics, setOpenUpdateBasics] = useState<boolean>(false);
    const [openUpdateAll, setOpenUpdateAll] = useState<boolean>(false);
    const [openOverride, setOpenOverride] = useState<boolean>(false);

    async function onConfirmEngineDelete() {
        try {
            await deleteEngine({
                variables: { input: { id: engine.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("Engine deleted");
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmDelete(false);
    }

    async function onConfirmEngineStart() {
        try {
            await startEngine({
                variables: { input: { id: engine.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("Engine started");
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmStart(false);
    }

    async function onConfirmEngineStop() {
        try {
            await stopEngine({
                variables: { input: { id: engine.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("Engine stopped");
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmStop(false);
    }

    function getStatusColors(status: string) {
        switch (status) {
            case "stopped":
            case "stopping":
                return "text-red-500 bg-red-50";
            case "deploying":
                return "text-blue-500 bg-blue-50";
            case "starting":
                return "text-green-500 bg-green-50";
            case "ingesting":
                return "bg-green-100 text-green-600";
            case "waiting":
                return "bg-green-200 text-green-700";
            case "playing":
                return "bg-green-600 text-white";
            case "error":
                return "border-[1px] text-red-500 border-red-500";
        }
    }

    const isAdmin = AuthService.isTHEOAdminSales();

    const env = process.env.REACT_APP_THEO_ENV as string;
    const anywhereCluster = env === "prod" ? "anywhere" : "anywhere-dev";

    function getGrafanaLink(): string {
        return `https://grafana.eu-west-3.cuteanteater.com/d/a6e9fd09-2649-42a1-bd98-08564bd5b471/engine?orgId=1&var-cluster=${anywhereCluster}&var-namespace=${engine.id}`;
    }

    function getLokiLink(): string {
        return `https://grafana.eu-west-3.cuteanteater.com/explore?orgId=1&left=%7B%22datasource%22:%22P982945308D3682D1%22,%22queries%22:%5B%7B%22refId%22:%22A%22,%22expr%22:%22%7Bcluster%3D%5C%22${anywhereCluster}%5C%22,%20namespace%3D%5C%22${engine.id}%5C%22%7D%20%7C%3D%20%60%60%22,%22queryType%22:%22range%22,%22datasource%22:%7B%22type%22:%22loki%22,%22uid%22:%22P982945308D3682D1%22%7D,%22editorMode%22:%22builder%22%7D%5D,%22range%22:%7B%22from%22:%22now-15m%22,%22to%22:%22now%22%7D%7D`;
    }

    function getDuration(start: string): string {
        const duration = moment.duration(moment().diff(start));

        // Get the days, hours, and minutes from the duration
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        // Format the output string
        return `${days}d ${hours}h ${minutes}m`;
    }

    return (
        <div
            className={`flex flex-row justify-between w-full rounded-md border-[1px] ${
                highlight === true ? "border-purple-600 bg-purple-50" : "border-slate-300"
            } px-2 py-1`}
            onMouseEnter={() => onHover()}
            onMouseLeave={() => onStopHover()}
        >
            <div className="flex flex-row w-full">
                <div className="mr-2 flex flex-col gap-5">
                    <div className="flex flex-col gap-1">
                        <div
                            className={`mr-2  w-20 justify-center ${getStatusColors(
                                engine.status,
                            )} font-semibold text-xs rounded-md py-[2px] px-2 flex row gap-1 items-center`}
                        >
                            {engine.status}
                        </div>
                        {engine.status !== "stopped" && engine.onlineSince && (
                            <Tooltip title="Online since">
                                <div className="text-[10px] text-slate-500 flex flex-row justify-center">
                                    {getDuration(engine.onlineSince)}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                    <div className="flex flex-row items-center justify-center w-full">
                        {engine.status === "stopped" ? (
                            <>
                                <button
                                    disabled={startLoading}
                                    onClick={() => setOpenConfirmStart(true)}
                                    className="text-green-500 ml-[-8px] flex flex-row items-center gap-1  rounded-md px-1 py-1 text-xs disabled:text-slate-300 disabled:bg-slate-100"
                                >
                                    <PlayArrow style={{ fontSize: "14px" }} /> Start
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    disabled={stopLoading}
                                    onClick={() => setOpenConfirmStop(true)}
                                    className="text-red-500  ml-[-8px] flex flex-row items-center gap-1  rounded-md px-1 py-1 text-xs disabled:text-slate-300 disabled:bg-slate-100"
                                >
                                    <Stop style={{ fontSize: "14px" }} /> Stop
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-col w-full gap-0">
                    <div className="flex flex-row justify-between w-full">
                        <div className="flex flex-row gap-1 items-center">
                            <div className="font-semibold mb-1">{engine.name}</div>
                            <div className="flex">
                                <div className="text-xs px-1  rounded-md bg-slate-200">{engine.version}</div>
                            </div>
                            {engine.drm && (
                                <div
                                    className={` text-orange-500  font-semibold text-xs rounded-md py-[2px] px-2 flex row gap-1 items-center`}
                                >
                                    <Lock style={{ fontSize: "12px" }} /> DRM
                                </div>
                            )}
                        </div>
                        <div className="flex flex-row gap-1">
                            <Tooltip title={"Edit"}>
                                <button
                                    className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                    onClick={() =>
                                        canRemoveAndUpdateEngine ? setOpenUpdateAll(true) : setOpenUpdateBasics(true)
                                    }
                                >
                                    <Edit style={{ fontSize: "14px" }} />
                                </button>
                            </Tooltip>
                            <Tooltip title={canRemoveAndUpdateEngine ? "Delete" : "Cannot delete engine: not stopped"}>
                                <button
                                    disabled={!canRemoveAndUpdateEngine}
                                    onClick={() => setOpenConfirmDelete(true)}
                                    className="text-red-500 hover:text-red-600 rounded-md px-1 py-[2px] text-xs  disabled:text-slate-300"
                                >
                                    <DeleteForeverOutlined style={{ fontSize: "14px" }} />
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                        <div className="text-[11px] text-gray-500 mb-1 flex flex-row gap-2 items-center">
                            <Tooltip title="Priority">
                                <div className="bg-yellow-100 rounded-full px-1 py-[0.5px] font-semibold">
                                    {engine.priority}
                                </div>
                            </Tooltip>
                            <div>
                                {engine.id} - <strong>Ingest:</strong>
                            </div>
                            <div>{ingests.find((ingest) => ingest.id === engine.ingestId)!.name}</div>
                        </div>
                    </div>
                    <div className="flex flex-row text-gray-500 text-[11px] gap-2">
                        <div className="flex flex-row items-center gap-1">
                            <PublicOutlined style={{ fontSize: "13px" }} color="inherit" /> {engine.region}
                        </div>
                        <div className="flex flex-row items-center gap-1">
                            <ListOutlined style={{ fontSize: "13px" }} color="inherit" /> {engine.quality.abrLadderId}
                        </div>
                        <Tooltip title="Encoder preset">
                            <div className="flex flex-row items-center gap-1">
                                <SettingsOutlined style={{ fontSize: "13px" }} color="inherit" /> {engine.encoderPreset}
                            </div>
                        </Tooltip>
                        {engine.outputs.hlsMpegTs && (
                            <Tooltip title="HLS MPEG-TS">
                                <div className="flex flex-row items-center gap-1">
                                    <HlsOutlined style={{ fontSize: "13px" }} color="inherit" /> HLS MPEG-TS
                                </div>
                            </Tooltip>
                        )}

                        {engine.daiAssetKey && engine.daiAssetKey.length > 0 && (
                            <Tooltip title="DAI asset key">
                                <div className="flex flex-row items-center gap-1">
                                    <KeyOutlined style={{ fontSize: "13px" }} color="inherit" /> {engine.daiAssetKey}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                    {showMore && (
                        <div className="rounded-md border-[1px] bg-gray-50 flex flex-col gap-1 mt-2 px-2 py-1 text-[12px]">
                            {isAdmin && (
                                <div className="mt-2">
                                    <div className="mt-2">
                                        <div className="flex flex-row gap-4 justify-between">
                                            <div className="font-semibold">Override</div>
                                            <button
                                                disabled={!canRemoveAndUpdateEngine}
                                                className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                                onClick={() => setOpenOverride(true)}
                                            >
                                                <Edit style={{ fontSize: "14px" }} />
                                            </button>
                                        </div>
                                        <div className="bg-white flex flex-col p-1">
                                            {engine.override?.image && engine.override?.image.trim().length > 0 ? (
                                                <div>
                                                    <strong>Image:</strong> {engine.override.image}
                                                </div>
                                            ) : (
                                                <div>Nothing set</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <div className="font-semibold">Admin links</div>
                                        <div className="mt-1 flex flex-row gap-3">
                                            <button
                                                className="rounded-md px-3 py-1 bg-yellow-300 hover:bg-yellow-200 font-semibold"
                                                onClick={() => {
                                                    window.open(getGrafanaLink());
                                                }}
                                            >
                                                <LinkOutlined style={{ fontSize: "15px" }} /> Grafana
                                            </button>
                                            <button
                                                className="rounded-md px-3 py-1 bg-yellow-300 hover:bg-yellow-200 font-semibold"
                                                onClick={() => {
                                                    window.open(getLokiLink());
                                                }}
                                            >
                                                <LinkOutlined style={{ fontSize: "15px" }} /> Loki
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="cursor-pointer" onClick={() => setShowMore(!showMore)}>
                {showMore ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </div>
            <ConfirmDeleteDialog
                title="Delete engine"
                content={
                    <div>
                        Are you sure you want to delete the engine <strong>{engine.name}</strong> (
                        <span className="font-mono">{engine.id}</span>)?
                    </div>
                }
                open={openConfirmDelete}
                onClose={() => setOpenConfirmDelete(false)}
                onConfirm={() => onConfirmEngineDelete()}
            />
            <ConfirmDeleteDialog
                title="Start engine"
                content={
                    <div>
                        Are you sure you want to start the engine <strong>{engine.name}</strong> (
                        <span className="font-mono">{engine.id}</span>)?
                    </div>
                }
                open={openConfirmStart}
                onClose={() => setOpenConfirmStart(false)}
                onConfirm={() => onConfirmEngineStart()}
            />
            <ConfirmDeleteDialog
                title="Stop engine"
                content={
                    <div>
                        Are you sure you want to stop the engine <strong>{engine.name}</strong> (
                        <span className="font-mono">{engine.id}</span>)?
                    </div>
                }
                open={openConfirmStop}
                onClose={() => setOpenConfirmStop(false)}
                onConfirm={() => onConfirmEngineStop()}
            />
            <UpdateEngineBasicsDialog
                open={openUpdateBasics}
                onClose={() => setOpenUpdateBasics(false)}
                engine={engine}
                organizationId={organizationId}
                channelId={channelId}
                onUpdated={(name, priority) => {
                    setOpenUpdateBasics(false);
                    setEngine({ ...engine, name, priority });
                }}
            />
            <UpdateEngineOverrideDialog
                open={openOverride}
                onClose={() => setOpenOverride(false)}
                engine={engine}
                organizationId={organizationId}
                channelId={channelId}
                onUpdated={({ image }) => {
                    setOpenOverride(false);
                    setEngine({ ...engine, override: { __typename: "EngineOverride", image } });
                }}
            />

            <UpdateEngineDialog
                open={openUpdateAll}
                onClose={() => setOpenUpdateAll(false)}
                engine={engine}
                organizationId={organizationId}
                channelId={channelId}
                abrLadders={channel.abrLadders}
                ingests={ingests.map((ingest) => ({
                    id: ingest.id,
                    name: ingest.name,
                    region: ingest.region !== null ? ingest.region : undefined,
                }))}
                regions={channel.regions}
                onUpdated={(values: EngineValues) => {
                    setOpenUpdateAll(false);
                    const { abrLadderId, drm, encoderPreset, ingestId, name, priority, region, daiAssetKey } = values;
                    setEngine({
                        ...engine,
                        name,
                        priority,
                        quality: {
                            __typename: "EngineQuality",
                            abrLadderId,
                        },
                        drm,
                        encoderPreset,
                        ingestId,
                        region,
                        daiAssetKey,
                    });
                }}
            />
        </div>
    );
}

export const UPDATE_V2_ENGINE = gql`
    mutation UpdateV2EngineMutation($input: UpdateV2EngineInput!) {
        updateV2Engine(input: $input)
    }
`;
